import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSaveCanadaPricing } from '@/hooks/useAdminWarehouseLookups'
import {
  CANADA_PRICING_DEFAULTS,
  CANADA_PRICING_KEYS,
  type WarehouseLookupEntry,
} from '@/types/adminWarehouseLookups'

const PREVIEW_US_RETAIL = 10

interface CanadaPricingSettingsCardProps {
  entries: WarehouseLookupEntry[]
  isLoading: boolean
}

function activeValue(
  entries: WarehouseLookupEntry[],
  key: string,
  fallback: number
): number {
  const entry = entries.find(e => e.lookup_key === key && e.is_active)
  return typeof entry?.value_num === 'number' ? entry.value_num : fallback
}

/** Strip float noise from a decimal → percent conversion (0.31 → 31). */
function toPercent(value: number): string {
  return String(Number((value * 100).toFixed(4)))
}

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export default function CanadaPricingSettingsCard({
  entries,
  isLoading,
}: CanadaPricingSettingsCardProps) {
  const saveMutation = useSaveCanadaPricing()

  const savedRate = activeValue(
    entries,
    CANADA_PRICING_KEYS.cadUsdRate,
    CANADA_PRICING_DEFAULTS.cadUsdRate
  )
  const savedCost = activeValue(
    entries,
    CANADA_PRICING_KEYS.costOfDoingBusiness,
    CANADA_PRICING_DEFAULTS.costOfDoingBusiness
  )

  const [rateInput, setRateInput] = useState(String(savedRate))
  const [costPctInput, setCostPctInput] = useState(toPercent(savedCost))

  useEffect(() => {
    setRateInput(String(savedRate))
    setCostPctInput(toPercent(savedCost))
  }, [savedRate, savedCost])

  const rate = Number(rateInput)
  const costPct = Number(costPctInput)
  const rateError =
    rateInput.trim() === '' || !Number.isFinite(rate) || rate <= 0
      ? 'Exchange rate must be greater than 0'
      : null
  const costError =
    costPctInput.trim() === '' || !Number.isFinite(costPct) || costPct < 0
      ? 'Cost of doing business must be 0% or more'
      : null
  const isValid = !rateError && !costError
  const isDirty = rate !== savedRate || Number(toPercent(savedCost)) !== costPct

  const preview = useMemo(() => {
    if (!isValid) return null
    return (PREVIEW_US_RETAIL / rate) * (1 + costPct / 100)
  }, [isValid, rate, costPct])

  const handleSave = () => {
    if (!isValid) return
    saveMutation.mutate({
      cadUsdRate: rate,
      costOfDoingBusiness: Number((costPct / 100).toFixed(6)),
    })
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4"
      data-testid="canada-pricing-settings"
    >
      <div>
        <h3 className="text-base font-semibold text-foreground">
          Canada Pricing
        </h3>
        <p className="text-xs text-muted-foreground">
          Canadian Pricing = US Final Retail ÷ exchange rate × (1 + cost of
          doing business)
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="canada-cad-usd-rate">
            CAD → USD exchange rate (USD per 1 CAD)
          </Label>
          <Input
            id="canada-cad-usd-rate"
            type="number"
            step="any"
            min="0"
            value={rateInput}
            disabled={isLoading}
            onChange={e => setRateInput(e.target.value)}
            aria-invalid={!!rateError}
          />
          {rateError && <p className="text-xs text-destructive">{rateError}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="canada-cost-of-doing-business">
            Cost of doing business in Canada (%)
          </Label>
          <Input
            id="canada-cost-of-doing-business"
            type="number"
            step="any"
            min="0"
            value={costPctInput}
            disabled={isLoading}
            onChange={e => setCostPctInput(e.target.value)}
            aria-invalid={!!costError}
          />
          {costError && <p className="text-xs text-destructive">{costError}</p>}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p
          className="text-sm text-muted-foreground"
          data-testid="canada-pricing-preview"
        >
          {preview === null
            ? 'Enter valid values to preview.'
            : `${currency.format(PREVIEW_US_RETAIL)} US retail → ${currency.format(preview)} CAD`}
        </p>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isValid || !isDirty || isLoading || saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Changes apply to line items as they are recalculated (e.g. after an edit
        or re-import).
      </p>
    </div>
  )
}
